import { useState } from "react";
import { Palette } from "lucide-react";
import { ThemePicker } from "./ThemePicker";

interface PageHeaderProps {
  activeNav: string;
  searchValue: string;
  onSearch: (value: string) => void;
  onEnter?: () => void;
  subtitle?: string;
  hideSearch?: boolean;
}

const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

function getTodayLabel() {
  const d = new Date();
  return {
    day: DAYS[d.getDay()],
    date: `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`,
  };
}

export function PageHeader({ activeNav, searchValue, onSearch, onEnter, subtitle, hideSearch }: PageHeaderProps) {
  const [showThemePicker, setShowThemePicker] = useState(false);
  const { day, date } = getTodayLabel();

  const paletteButton = (
    <button
      onClick={() => setShowThemePicker((v) => !v)}
      aria-label="Change mood theme"
      aria-pressed={showThemePicker}
      title="Change mood theme"
      data-testid="btn-theme-toggle"
      className={
        "flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-400 focus-visible:ring-offset-1 " +
        (showThemePicker
          ? "border-stone-900 bg-stone-900 text-white"
          : "border-stone-200 bg-white text-stone-400 hover:border-stone-300 hover:text-stone-700")
      }
    >
      <Palette className="h-4 w-4" />
    </button>
  );

  return (
    <header className="mb-6 rounded-3xl border border-stone-200 bg-white p-4 shadow-sm md:p-5" data-testid="page-header">
      {activeNav === "Daily" ? (
        <div className="flex items-center justify-between gap-3 md:gap-6">
          <div className="flex items-baseline gap-2 md:gap-4 min-w-0">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-serif italic tracking-tight text-stone-900 shrink-0">
              {day}
            </h2>
            <span className="text-xs sm:text-sm font-medium text-stone-400 truncate">{date}</span>
            <span className="hidden md:block w-px h-5 bg-stone-200 self-center shrink-0" />
            <p className="hidden md:block text-sm text-stone-500 truncate">
              A quote to discover. A quote to remember.
            </p>
          </div>
          {paletteButton}
        </div>
      ) : (
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            {subtitle && <p className="mb-1 text-sm font-medium text-stone-500">{subtitle}</p>}
            <h2 className="text-2xl font-semibold tracking-tight md:text-3xl text-stone-900">
              {activeNav === "Search" ? "Search results" : activeNav}
            </h2>
          </div>
          <div className="flex items-center gap-2">
            {!hideSearch && (
              <div className="flex flex-1 items-center gap-2 rounded-2xl border border-stone-200 bg-white px-3 py-2 focus-within:border-stone-400 focus-within:ring-1 focus-within:ring-stone-400 transition-all">
                <span className="text-stone-400 font-medium select-none">⌕</span>
                <input
                  className="w-full bg-transparent text-sm outline-none placeholder:text-stone-400 md:w-64 text-stone-900"
                  placeholder="Search quotes, authors…"
                  data-testid="input-search"
                  value={searchValue}
                  onChange={(e) => onSearch(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") onEnter?.();
                  }}
                />
                {searchValue && (
                  <button
                    onClick={() => onSearch("")}
                    className="flex-shrink-0 text-stone-400 hover:text-stone-600 transition cursor-pointer focus-visible:outline-none focus-visible:ring-1 focus-within:ring-stone-400 rounded leading-none"
                    aria-label="Clear search"
                    data-testid="btn-search-clear"
                  >
                    ×
                  </button>
                )}
              </div>
            )}
            {paletteButton}
          </div>
        </div>
      )}
      {showThemePicker && (
        <div className="mt-4 border-t border-stone-100 pt-4">
          <ThemePicker compact />
        </div>
      )}
    </header>
  );
}
